import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://gtbuyspreadsheet.run";
const logoUrl = "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/54d75be42d5913aa14ca64dd22e5d1e3.jpeg";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "How to Use GTBuy Spreadsheet — Step-by-Step Guide",
  description: "A practical walkthrough for setting up and mastering the GTBuy Spreadsheet system. Follow clear steps to organize supplier data and build a repeatable sourcing workflow.",
  datePublished: "2026-07-01",
  dateModified: "2026-07-01",
  author: {
    "@type": "Organization",
    name: "GTBuy Spreadsheet",
    url: siteUrl,
  },
  publisher: {
    "@type": "Organization",
    name: "GTBuy Spreadsheet",
    logo: {
      "@type": "ImageObject",
      url: logoUrl,
    },
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `${siteUrl}/how-to-use-gtbuy`,
  },
};

export default function HowToUseGTBuy() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <Navbar />
      <main>
        <section className="w-full pt-24 pb-12 md:pt-32 md:pb-16 px-6 md:px-10 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-5">
              <Link to="/" className="text-sm text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer flex items-center gap-1"><i className="ri-arrow-left-line"></i> Back to Home</Link>
              <span className="text-foreground-300">/</span>
              <span className="text-sm text-foreground-500">Tutorial</span>
            </div>
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-4">Tutorial &middot; 6 min read</span>
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-tight mb-4">How to Use GTBuy Spreadsheet — Step-by-Step Guide</h1>
            <p className="text-base text-foreground-600 leading-relaxed max-w-2xl">A practical walkthrough for setting up and mastering the GTBuy Spreadsheet system. Follow clear steps to organize supplier data, compare pricing efficiently, and build a repeatable sourcing workflow.</p>
            <p className="text-xs text-foreground-400 mt-4">Published: July 2026</p>
          </div>
        </section>

        <section className="w-full px-6 md:px-10 pb-10">
          <div className="max-w-4xl mx-auto">
            <div className="aspect-[16/7] w-full rounded-2xl overflow-hidden bg-background-200">
              <img src="https://readdy.ai/api/search-image?query=Overhead%20shot%20of%20hands%20actively%20typing%20on%20laptop%20with%20spreadsheet%20template%20clearly%20visible%20on%20screen%20showing%20product%20rows%20and%20supplier%20columns%20with%20colorful%20cells%20coffee%20and%20notepad%20on%20warm%20wooden%20desk%20natural%20lighting%20instructional%20photography%20high%20detail&width=1600&height=700&seq=article-how-to-use-header&orientation=landscape"
                alt="How to Use GTBuy Spreadsheet" title="How to Use GTBuy Spreadsheet — Step-by-Step" className="w-full h-full object-cover" loading="eager" />
            </div>
          </div>
        </section>

        <article className="w-full px-6 md:px-10 pb-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-10 mb-4">Getting Started: What You Need</h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">The GTBuy Spreadsheet system works with any modern spreadsheet application — Google Sheets, Microsoft Excel, or Apple Numbers. <strong>No special software or technical skills required.</strong> If you can use basic spreadsheet functions, you have everything needed to implement this system.</p>

            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-12 mb-4">Step-by-Step Setup</h2>
            <ol className="list-decimal list-inside space-y-4 mb-8 text-base text-foreground-700 leading-relaxed">
              <li><strong>Create your master product sheet.</strong> Start with columns for Product Name, SKU, Category, Target Price, and Notes. This becomes your catalog backbone.</li>
              <li><strong>Build a supplier directory sheet.</strong> Add columns for Supplier Name, Contact Info, Payment Terms, Lead Time, and Reliability Score. Link suppliers to products using a shared ID column.</li>
              <li><strong>Set up a comparison sheet.</strong> For each product, create rows for at least three supplier quotes. Include Unit Price, MOQ, Shipping Cost, and Total Landed Cost columns.</li>
              <li><strong>Add pricing formulas.</strong> Use simple SUM and AVERAGE formulas to auto-calculate landed costs and highlight the best supplier option automatically.</li>
              <li><strong>Establish a review rhythm.</strong> Schedule a weekly 20-minute session to update prices, add new supplier quotes, and archive outdated data.</li>
            </ol>

            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-12 mb-4">Pro Tips for Advanced Users</h2>
            <ul className="list-disc list-inside space-y-3 mb-8 text-base text-foreground-700 leading-relaxed">
              <li>Use <strong>conditional formatting</strong> to color-code cells — green for best price, yellow for median, red for above average.</li>
              <li>Create a <strong>separate historical archive sheet</strong> to track how supplier prices change over time. This data is invaluable for negotiation.</li>
              <li>Add a <strong>seasonal trends tab</strong> to note when certain products or suppliers offer better pricing, helping you time bulk orders strategically.</li>
            </ul>

            <p className="text-sm text-foreground-500 italic mb-4">
              Continue learning:{" "}
              <Link to="/what-is-gtbuy-spreadsheet" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">What is GTBuy?</Link>{" "}&middot;{" "}
              <Link to="/improve-profit-margins" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">Improve Profit Margins</Link>{" "}&middot;{" "}
              <Link to="/" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">Homepage</Link>
            </p>
          </div>
        </article>

        <section className="relative w-full py-20 px-6 md:px-10 bg-foreground-950 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-background-50 mb-4">Ready to Try the GTBuy System?</h2>
            <p className="text-foreground-400 text-sm mb-6">Browse products across 14 categories on KA Spreadsheet and put your new sourcing workflow into action.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">Shop on KA Spreadsheet</a>
              <Link to="/" className="px-6 py-3 rounded-full bg-foreground-800 text-background-50 font-medium text-sm hover:bg-foreground-700 transition-colors cursor-pointer whitespace-nowrap">Back to Homepage</Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}